import { findShapeIdsInsideFrame } from './expandSelectionForAiContext.js';
import { isCanvasEvidenceShape } from './pasteCanvasEvidence.js';
import { logicalFrameIdFromShapeId, membershipIdFromFrameShape } from './frameMembership.js';

/**
 * Expand a delete selection so frames take every object inside them:
 * tldraw children, associated (page-level) pins, spatially contained shapes,
 * and connectors that only bind those objects.
 */
export function expandSelectionForCanvasDelete(editor, selected = []) {
  if (!editor) return [];
  const byId = new Map();
  for (const shape of selected || []) {
    if (!shape?.id || byId.has(shape.id)) continue;
    byId.set(shape.id, shape);
    if (shape.type !== 'frame') continue;
    for (const childId of findShapeIdsInsideFrame(editor, shape)) {
      if (byId.has(childId)) continue;
      const child = editor.getShape?.(childId);
      if (child?.id) byId.set(child.id, child);
    }
  }
  return [...byId.values()];
}

/**
 * Digest sources that `/digest-event` should revoke in one batched POST.
 * User pins → activityId; chat pins, pasted/typed evidence, labeled lines,
 * and frames (cluster claims) → shapeId (+ frame logical aliases).
 */
export function collectDigestDeleteTargets(shapes = []) {
  const activityIds = [];
  const shapeIds = [];
  const seenPins = new Set();
  const seenShapes = new Set();

  const addPin = (id) => {
    const pinId = String(id || '').trim();
    if (!pinId || seenPins.has(pinId)) return;
    seenPins.add(pinId);
    activityIds.push(pinId);
  };
  const addShape = (id) => {
    const shapeId = String(id || '').trim();
    if (!shapeId || seenShapes.has(shapeId)) return;
    seenShapes.add(shapeId);
    shapeIds.push(shapeId);
  };

  for (const shape of shapes || []) {
    if (!shape) continue;
    if (shape.type === 'pin-card') {
      addPin(shape.meta?.activityId);
      continue;
    }
    if (shape.type === 'suggest-pin') continue;
    if (shape.type === 'conversation-pin') {
      addShape(shape.id);
      continue;
    }
    if (shape.type === 'frame') {
      addShape(shape.id);
      const membershipId = membershipIdFromFrameShape(shape);
      if (membershipId) addShape(membershipId);
      const logical = logicalFrameIdFromShapeId(shape.id);
      if (logical) addShape(logical);
      continue;
    }
    if (shape.type === 'arrow') {
      addShape(shape.id);
      continue;
    }
    if (isCanvasEvidenceShape(shape)) addShape(shape.id);
  }

  return { activityIds, shapeIds };
}
